// Full-screen-friendly modal that mounts the per-category intake form.
// Used by `<QuoteModalProvider>` — do NOT render this directly.
//
// Category → intake mapping:
//   insurance                 → IntakeInsurance (3 fields)
//   auto-loan-new / -used     → IntakeAutoLoan (4 fields, defaults to "new"/"used")
//   refinance                 → IntakeAutoLoan with defaultLoanType="refinance"
//   warranty                  → IntakeWarranty (4 fields)
//   (anything else / unknown) → legacy QuoteForm fallback (ZIP-only)
//
// Universal-category tabs (added per the Phase 4 modal UX spec):
//   - Top-level tabs: Insurance · Auto Loans · Refinance · Warranties
//   - Auto Loans expands to a sub-toggle: New · Used
//   - Switching tabs is free (no modal close, no data loss in other tabs —
//     each intake owns its own localStorage-backed draft per category)
//   - Every switch fires a MODAL_CATEGORY_SWITCH event for funnel analytics
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  X,
  Lock,
  ShieldCheck,
  Clock,
  Users,
  Banknote,
  Car,
  CarFront,
  CarTaxiFront,
  Wrench,
  TrendingDown,
  Sparkles,
} from "lucide-react";
import QuoteForm from "./QuoteForm";
import IntakeInsurance from "./intake/IntakeInsurance";
import IntakeAutoLoan from "./intake/IntakeAutoLoan";
import IntakeWarranty from "./intake/IntakeWarranty";
import { track, events } from "../lib/analytics";

// Iter-166 — Step labels unified across all categories via the shared
// FunnelProgress component (Enter Details → Matching Providers →
// Compare Options). The previous per-category STEP_LABELS map has been
// removed — the user spec mandates consistent journey markers across
// every entry surface (homepage form + every modal).

// Per-category modal headline + sub. Used when no caller override has been
// passed — keeps the modal copy on-brand for the user's actual intent.
const COPY = {
  insurance: {
    headline: "Compare auto insurance in about 2 minutes.",
    sub: "Enter a few details and compare insurance options available in your area. Free to compare. No spam calls.",
  },
  "auto-loan-new": {
    headline: "Find your lowest new-car APR.",
    sub: "Pre-qualify with a soft credit pull. Compare offers before you walk in the dealership.",
  },
  "auto-loan-used": {
    headline: "Find your lowest used-car APR.",
    sub: "Pre-qualify with a soft credit pull. See which lenders quote on your purchase price, not dealer markup.",
  },
  refinance: {
    headline: "Lower your existing car payment.",
    sub: "A 1–2 point APR drop saves $700–$1,800 over a 60-month loan. Soft credit pull, no obligation.",
  },
  warranty: {
    headline: "Compare extended warranty plans.",
    sub: "We'll match you with vetted plans — no dealer F&I markup, no spam, no hard pull.",
  },
};

// Top-level tabs surfaced inside the modal. Order matters — runs left to
// right on desktop, wraps to 2 rows on mobile.
const TABS = [
  { id: "insurance", label: "Insurance", Icon: ShieldCheck },
  { id: "auto-loan", label: "Auto Loans", Icon: Car, expandsTo: "auto-loan-new" },
  { id: "refinance", label: "Refinance", Icon: TrendingDown },
  { id: "warranty", label: "Warranties", Icon: Wrench },
];

// Cross-vertical nudge copy — shown once per session after the first
// in-modal category switch. Reinforces the "save money on car ownership"
// positioning and lifts cross-vertical conversion (industry sees ~10-15%
// relative gain on modal→submit when bundle nudges are present).
//
// Iter-122 (Sprint C / M-1) — Replaced unsourced "save $312/yr" +
// "save $200+/yr" dollar claims with educational framing. The previous
// numbers had no provenance in the data_sources registry and tripped
// the affiliate-audit M-1 finding. We keep the cross-vertical nudge
// (it lifts conversion) but anchor it on the underlying industry
// behavior (multi-policy discount, side-by-side comparison) instead of
// a fabricated dollar figure.
const NUDGE_COPY = {
  insurance:
    "Most insurers offer a multi-policy discount when you bundle home or auto with another product — want to compare both?",
  "auto-loan-new":
    "Pre-qualify with a soft credit pull — then re-shop your insurance side-by-side.",
  "auto-loan-used":
    "Used-car buyers often re-shop insurance the same week. Compare both in one flow?",
  refinance:
    "Many drivers compare warranty, insurance, and financing options together to lower overall ownership costs.",
  warranty:
    "Pair a warranty quote with your insurance comparison — no dealer F&I markup, no calls.",
};

function pickNudgeCategory(from, to) {
  // Show the nudge tuned to the user's current intent (the tab they
  // landed on, not the one they came from). Keeps the message anchored
  // to what they actually want next, not what they just abandoned.
  return NUDGE_COPY[to] ? to : null;
}

// Map an internal `category` value to (top-tab, sub-tab). The `sub` value
// is only meaningful when `top === "auto-loan"`.
function categoryToTab(category) {
  if (category === "auto-loan-new") return { top: "auto-loan", sub: "new" };
  if (category === "auto-loan-used") return { top: "auto-loan", sub: "used" };
  if (category === "insurance") return { top: "insurance", sub: null };
  if (category === "refinance") return { top: "refinance", sub: null };
  if (category === "warranty") return { top: "warranty", sub: null };
  return { top: "insurance", sub: null };
}

// Reverse: (top, sub) → canonical category string consumed by the rest of
// the stack (intake forms, analytics, partner routing).
function tabToCategory(top, sub) {
  if (top === "auto-loan") return sub === "used" ? "auto-loan-used" : "auto-loan-new";
  return top;
}

function pickIntake(category) {
  if (category === "insurance") {
    return <IntakeInsurance />;
  }
  if (category === "refinance") {
    return <IntakeAutoLoan defaultLoanType="refinance" />;
  }
  if (category === "auto-loan-new") {
    return <IntakeAutoLoan defaultLoanType="new" />;
  }
  if (category === "auto-loan-used") {
    return <IntakeAutoLoan defaultLoanType="used" />;
  }
  if (category === "warranty") {
    return <IntakeWarranty />;
  }
  return null;
}

export default function QuoteModal({
  isOpen,
  category,
  headlineOverride,
  subOverride,
  successContext,
  onClose,
}) {
  // The modal owns its own active-tab state so users can flip categories
  // inside the modal without losing draft data in the other forms. We
  // initialize from the provider-supplied `category` prop and reconcile
  // whenever it changes (e.g. modal closed + re-opened with a different
  // intent).
  const initial = useMemo(() => categoryToTab(category), [category]);
  const [topTab, setTopTab] = useState(initial.top);
  const [autoSub, setAutoSub] = useState(initial.sub || "new");
  // Mid-modal nudge: shown for ~10s after the first category switch in a
  // modal session. `nudgeShown` (a ref) ensures we never show it more than
  // once per modal-open lifecycle. `nudgeCategory` controls the message
  // body — null = hidden.
  const [nudgeCategory, setNudgeCategory] = useState(null);
  const nudgeShown = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    setTopTab(initial.top);
    setAutoSub(initial.sub || "new");
    setNudgeCategory(null);
    nudgeShown.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, category]);

  // Lock body scroll while modal is open + close on ESC.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  // Compute the canonical category from the active tab.
  const activeCategory = tabToCategory(topTab, autoSub);

  // Memoize the intake form by the active category — so flipping tabs
  // unmounts and remounts the correct form (intake-internal localStorage
  // hydration takes care of restoring data when the user flips back).
  const intake = useMemo(() => pickIntake(activeCategory), [activeCategory]);
  // Iter-166 — Step labels are now unified at the FunnelProgress
  // component level (Enter Details → Matching Providers → Compare
  // Options). The activeCategory still drives the modal headline + sub
  // below.
  const copy = COPY[activeCategory] || {};

  const handleTopTabClick = (nextTopId) => {
    if (nextTopId === topTab) return;
    setTopTab(nextTopId);
    const nextCat =
      nextTopId === "auto-loan"
        ? tabToCategory("auto-loan", autoSub)
        : tabToCategory(nextTopId, null);
    track(events.MODAL_CATEGORY_SWITCH, {
      modal_name: "quote_modal",
      from: activeCategory,
      to: nextCat,
      surface: "top_tab",
    });
    // Mid-modal cross-vertical nudge — fire ONCE per modal session, the
    // first time the user proves they're shopping cross-category. This is
    // the highest-intent moment in the modal funnel.
    if (!nudgeShown.current) {
      const cat = pickNudgeCategory(activeCategory, nextCat);
      if (cat) {
        setNudgeCategory(cat);
        nudgeShown.current = true;
      }
    }
  };

  const handleSubTabClick = (nextSub) => {
    if (nextSub === autoSub && topTab === "auto-loan") return;
    setAutoSub(nextSub);
    const nextCat = tabToCategory("auto-loan", nextSub);
    track(events.MODAL_CATEGORY_SWITCH, {
      modal_name: "quote_modal",
      from: activeCategory,
      to: nextCat,
      surface: "sub_tab",
    });
  };

  // Iter-178 — Swipe-to-switch-tabs gesture for mobile users (mirrors
  // the Robinhood/Cash App portfolio-tabs micro-pattern). Horizontal
  // swipe left = next tab; swipe right = previous tab. Vanilla touch
  // events (no framer-motion dep) so:
  //   • Bundle size stays put.
  //   • We have full control over reduced-motion respect.
  //   • Form inputs + scrolling don't fight the handler.
  // Guards:
  //   • prefers-reduced-motion: reduce → handler disabled entirely.
  //   • Target is an input/textarea/select/button → no-op (preserves
  //     existing tap + form interaction semantics).
  //   • Vertical drift > 60px → treated as scroll, not swipe.
  //   • Horizontal distance < 60px → noise threshold, ignored.
  //   • Gesture took > 800ms → treated as drag, not swipe.
  const swipeRef = useRef({ startX: 0, startY: 0, startT: 0, active: false });
  const swipeReducedMotion = useRef(false);
  // Iter-179 — One-time discovery hint. Shows a subtle "← swipe to
  // switch →" pill for ~2.5s on the user's first mobile-modal open so
  // they discover the gesture; persisted via localStorage so it never
  // reappears for returning users.
  const SWIPE_HINT_KEY = "carsavr.modal.swipe_hint.v1";
  const [showSwipeHint, setShowSwipeHint] = useState(false);
  useEffect(() => {
    swipeReducedMotion.current =
      typeof window !== "undefined" &&
      !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  }, []);
  useEffect(() => {
    if (!isOpen) return undefined;
    if (typeof window === "undefined") return undefined;
    if (swipeReducedMotion.current) return undefined;
    // Mobile-only check — desktop already shows all 4 tabs clearly.
    const isMobile = window.matchMedia?.("(max-width: 639px)").matches;
    if (!isMobile) return undefined;
    let seen = false;
    try {
      seen = window.localStorage?.getItem(SWIPE_HINT_KEY) === "1";
    } catch {
      // localStorage might be blocked (private mode); treat as not-seen.
    }
    if (seen) return undefined;
    // Brief delay so the modal entrance animation lands before the hint
    // surfaces — avoids the hint competing with the modal slide-in.
    const showT = setTimeout(() => setShowSwipeHint(true), 400);
    const hideT = setTimeout(() => {
      setShowSwipeHint(false);
      try {
        window.localStorage?.setItem(SWIPE_HINT_KEY, "1");
      } catch {
        // No-op on storage-blocked browsers.
      }
    }, 3000);
    return () => {
      clearTimeout(showT);
      clearTimeout(hideT);
    };
  }, [isOpen]);

  const switchToTopTabAtOffset = (offset) => {
    const idx = TABS.findIndex((t) => t.id === topTab);
    if (idx === -1) return;
    const next = TABS[(idx + offset + TABS.length) % TABS.length];
    if (!next || next.id === topTab) return;
    handleTopTabClick(next.id);
    track(events.MODAL_CATEGORY_SWITCH, {
      modal_name: "quote_modal",
      from: topTab,
      to: next.id,
      surface: "swipe_gesture",
    });
  };

  const swipeProps = {
    onTouchStart: (e) => {
      if (swipeReducedMotion.current) return;
      const tag = (e.target?.tagName || "").toUpperCase();
      // Ignore touches that begin on a form control — the user is
      // about to interact with it, not navigate.
      if (["INPUT", "TEXTAREA", "SELECT", "BUTTON"].includes(tag)) {
        swipeRef.current.active = false;
        return;
      }
      const t = e.touches[0];
      swipeRef.current = {
        startX: t.clientX,
        startY: t.clientY,
        startT: Date.now(),
        active: true,
      };
    },
    onTouchEnd: (e) => {
      const s = swipeRef.current;
      if (!s.active) return;
      s.active = false;
      const touch = e.changedTouches[0];
      const dx = touch.clientX - s.startX;
      const dy = touch.clientY - s.startY;
      const dt = Date.now() - s.startT;
      // Horizontal swipe envelope.
      if (Math.abs(dx) < 60) return;
      if (Math.abs(dy) > 60) return;
      if (dt > 800) return;
      // dx < 0 → finger moved left → user wants the NEXT (right) tab.
      switchToTopTabAtOffset(dx < 0 ? 1 : -1);
    },
  };

  if (!isOpen) return null;

  return (
    <div
      data-testid="quote-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="quote-modal-title"
      className="fixed inset-0 z-[120] grid place-items-center px-4 py-8 bg-slate-950/70 backdrop-blur-sm overflow-y-auto"
      onClick={(e) => {
        // Backdrop click only — not bubbled clicks from inside the card.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative w-full max-w-xl my-auto">
        <button
          type="button"
          onClick={onClose}
          data-testid="quote-modal-close"
          aria-label="Close quote modal"
          className="absolute top-4 right-4 z-10 w-11 h-11 grid place-items-center bg-white/0 hover:bg-slate-100 text-slate-500 hover:text-slate-950 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        <div
          className="bg-white shadow-2xl"
          data-testid="quote-modal-swipe-surface"
          {...swipeProps}
        >
          <header className="px-7 pt-8 pb-6 border-b border-slate-100">
            <p className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-[#10B981] mb-3">
              <span
                aria-hidden
                className="relative flex w-1.5 h-1.5"
              >
                <span className="absolute inset-0 rounded-full bg-[#10B981] animate-ping opacity-75" />
                <span className="relative inline-flex rounded-full w-1.5 h-1.5 bg-[#10B981]" />
              </span>
              {/* Iter-177 — On mobile the longer "No hard credit pull" phrase
                  wrapped to a second line + made the eyebrow visually noisy.
                  Trim the mobile eyebrow to 3 short tokens; show the full
                  signal on sm+ screens where the line fits. */}
              <span className="sm:hidden" data-testid="quote-modal-eyebrow-mobile">
                Free · 2 min · No spam
              </span>
              <span className="hidden sm:inline" data-testid="quote-modal-eyebrow-desktop">
                Free · 2 min · No hard credit pull · No spam
              </span>
            </p>
            <h2
              id="quote-modal-title"
              data-testid="quote-modal-title"
              className="font-display font-black text-3xl text-slate-950 leading-[1.1] tracking-tight"
            >
              {headlineOverride || copy.headline || "Compare rates in about 2 minutes."}
            </h2>
            <p
              data-testid="quote-modal-sub"
              className="ph-mask mt-2 text-sm text-slate-600 leading-relaxed"
            >
              {subOverride ||
                copy.sub ||
                "Tell us a few details — we'll route you to the best match in your category."}
            </p>

            {/* Mid-modal cross-vertical nudge — appears once per session
                after the user's first category switch. Reinforces the
                bundling value prop at the highest-intent moment. */}
            {nudgeCategory && (
              <div
                data-testid="quote-modal-nudge"
                role="status"
                className="mt-4 inline-flex items-start gap-2 bg-[#10B981]/15 border border-[#10B981]/30 px-3 py-2.5 text-xs text-slate-950 font-medium leading-snug"
              >
                <Sparkles className="w-3.5 h-3.5 text-[#10B981] mt-0.5 shrink-0" />
                <span>{NUDGE_COPY[nudgeCategory]}</span>
                <button
                  type="button"
                  onClick={() => setNudgeCategory(null)}
                  data-testid="quote-modal-nudge-dismiss"
                  aria-label="Dismiss bundling tip"
                  className="ml-1 text-slate-500 hover:text-slate-950 shrink-0"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}

            {/* CATEGORY TABS — universal CarSavr modal tab system.
                Lets a visitor switch product intent inside the modal
                without closing it. Each tab preserves its own draft via
                IntakeShell's localStorage layer. */}
            <div
              role="tablist"
              aria-label="Quote categories"
              data-testid="quote-modal-tabs"
              className="mt-5 flex sm:grid sm:grid-cols-4 gap-1 bg-slate-100 p-1"
            >
              {TABS.map((t) => {
                const isActive = topTab === t.id;
                const Icon = t.Icon;
                return (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-label={t.label}
                    title={t.label}
                    data-testid={`quote-modal-tab-${t.id}`}
                    onClick={() => handleTopTabClick(t.id)}
                    className={`min-h-[44px] py-2 text-[11px] sm:text-xs font-bold flex items-center justify-center gap-1.5 transition-colors ${
                      isActive
                        ? "flex-1 sm:flex-none px-3 bg-slate-950 text-white"
                        : "w-11 sm:w-auto sm:px-1.5 shrink-0 bg-transparent text-slate-600 hover:text-slate-950"
                    }`}
                  >
                    {/* Iter-177 — Robinhood/Mercury mobile pattern:
                          • Mobile: ACTIVE tab gets `flex-1` so it absorbs the
                            leftover horizontal space, INACTIVE tabs are fixed
                            44×44 icon squares. The active label always fits.
                          • Desktop: 4-equal grid preserved (sm:grid-cols-4).
                        Eliminates the iter-176 "Insura..." / "Auto L..." /
                        "Refin..." / "Warra..." truncation entirely + makes the
                        selected category obvious by both dark fill AND full
                        label visibility. */}
                    <Icon className="w-4 h-4 sm:w-3.5 sm:h-3.5 shrink-0" />
                    <span
                      className={`truncate ${isActive ? "inline" : "hidden sm:inline"}`}
                      data-testid={`quote-modal-tab-${t.id}-label`}
                    >
                      {t.label}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Iter-179 — One-time swipe-discovery hint. Mobile-only,
                fades in after the modal lands, holds ~2.6s, fades out,
                then localStorage suppresses it forever. */}
            {showSwipeHint && (
              <p
                data-testid="quote-modal-swipe-hint"
                aria-live="polite"
                className="sm:hidden mt-2 text-center text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 animate-[carsavrSwipeHintFade_2600ms_ease-in-out_forwards] pointer-events-none"
              >
                ← Swipe to switch →
              </p>
            )}
            <style>{`
              @keyframes carsavrSwipeHintFade {
                0%   { opacity: 0; transform: translateY(-4px); }
                12%  { opacity: 1; transform: translateY(0); }
                80%  { opacity: 1; transform: translateY(0); }
                100% { opacity: 0; transform: translateY(0); }
              }
            `}</style>

            {/* AUTO-LOAN SUB-TABS — only render when Auto Loans is active.
                Toggles between New and Used car loan funnels. */}
            {topTab === "auto-loan" && (
              <div
                role="radiogroup"
                aria-label="New or used car loan"
                data-testid="quote-modal-sub-tabs"
                className="mt-3 grid grid-cols-2 border border-slate-200 bg-white"
              >
                {[
                  { id: "new", label: "New Car", Icon: CarFront },
                  { id: "used", label: "Used Car", Icon: CarTaxiFront },
                ].map((opt) => {
                  const isActive = autoSub === opt.id;
                  const Icon = opt.Icon;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      role="radio"
                      aria-checked={isActive}
                      data-testid={`quote-modal-sub-${opt.id}`}
                      onClick={() => handleSubTabClick(opt.id)}
                      className={`min-h-[44px] px-3 py-2 text-xs font-bold flex items-center gap-1.5 justify-center transition-colors ${
                        isActive
                          ? "bg-slate-950 text-white"
                          : "bg-white text-slate-600 hover:text-slate-950"
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5 shrink-0" />
                      <span>{opt.label}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Iter-167 — Progress indicator was duplicated here.
                Removed. The single shared FunnelProgress now lives in
                IntakeShell (rendered just above the form fields) which
                is the spec-mandated placement:
                  Tabs → Progress → Fields.
                IntakeShell also owns the matching + ready state, so
                rendering it there is the right home for the state
                machine too. */}
          </header>
          {/* Iter-167 — Bumped top padding to add 20-24px breathing
              room between the category tabs (in the header above) and
              the FunnelProgress + form fields below. Spec target:
              16-24px vertical spacing between tabs / progress / fields. */}
          <div className="px-7 pt-8 pb-7">
            {intake || (
              <QuoteForm
                id="modal-quote-form"
                defaultCategory={activeCategory}
                successContext={successContext}
                compact
              />
            )}
          </div>

          {/* TRUST RIBBON — security + transparency. Pinned to the modal's
              bottom border so it's visible without scrolling on most desktop
              sizes. Iter-78: replaced fabricated "2.4k drivers this week"
              claim (no real data behind it) with the editorial-standards
              chip — a verifiable truth (every lender + insurer on the site
              IS reviewed by our editors per /editorial-standards). Adds a
              "How we make money" link below the chips for transparency. */}
          <footer
            data-testid="quote-modal-trust"
            className="px-7 py-3 border-t border-slate-100 bg-[#FAFAFA] text-[10px] font-semibold text-slate-500"
          >
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-2">
              <TrustChip Icon={Lock} label="SSL secured" />
              <TrustChip Icon={ShieldCheck} label="No hard pull" />
              <TrustChip Icon={Clock} label="2-min compare" />
              <TrustChip Icon={Users} label="Editor-vetted" />
            </div>
            <p
              data-testid="quote-modal-transparency-link"
              className="text-[10px] text-slate-500 text-center pt-1 border-t border-slate-200"
            >
              Free for you · We earn a commission from partners.{" "}
              <Link
                to="/how-we-make-money"
                className="text-slate-700 hover:text-slate-950 underline-offset-2 hover:underline font-semibold"
                onClick={() => onClose?.()}
              >
                How we make money
              </Link>
            </p>
          </footer>
        </div>
      </div>
    </div>
  );
}

function TrustChip({ Icon, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 leading-tight">
      <Icon className="w-3 h-3 text-[#10B981] shrink-0" strokeWidth={2.5} />
      <span className="truncate">{label}</span>
    </span>
  );
}
